import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getA11yAttributesV2 } from '../../services/a11yIIBScreenReaderService';

import navigationService from '../../services/NavigationService';
import sessionReader from '../../store/readHelpers/SessionReader';
import OptionsTopBar from './OptionsTopBar';
import OptionsGroup from './OptionsGroup';
import ContrastButton from './optionButtons/ContrastButton';
import InstructionsButton from './optionButtons/InstructionsButton';
import ZoomFactorButton from './optionButtons/ZoomFactorButton';
import ExtraTimeButton from './optionButtons/ExtraTimeButton';
import SubmitButton from './optionButtons/SubmitButton';
import HideTimeRemainingButton from './optionButtons/HideTimeRemainingButton';
import LanguageButton from './optionButtons/LanguageButton';
import TTSButton from './optionButtons/TTSButton';
import InvigilatorActionsButton from './optionButtons/InvigilatorActionsButton';
import DeliverBlankButton from './optionButtons/DeliverBlankButton';
import ResumePasswordButton from './optionButtons/ResumePasswordButton';
import WithdrawButton from './optionButtons/WithdrawButton';
import ExitChromeAppButton from './optionButtons/ExitChromeAppButton';
import ExitSebButton from './optionButtons/ExitSebButton';
import FileDeliveryButton from './optionButtons/FileDeliveryButton';
import FileDeliveryChromeAppButton from './optionButtons/FileDeliveryChromeAppButton';
import BackToDashboardButton from './optionButtons/BackToDashboardButton';

import styles from './options.scss';
import optionsListSelector from '../../selectors/options/OptionsListSelector';
import { t } from '../../services/i18nService';

const CandidateOptions = (props) => {
    const {
        isAddExtraTimeAvailable,
        isBackToDashboardOptionAvailable,
        isContrastChooserAvailable,
        isDeliverBlankOptionAvailable,
        isDeliverOptionAvailable,
        isExitChromeAppAvailable,
        isExitSebOptionAvailable,
        isFileDeliveryChromeAppOptionAvailable,
        isFileDeliveryOptionAvailable,
        isHideTimeRemainingAvailable,
        isInstructionsOptionAvailable,
        isInvigilatorActionsOptionAvailable,
        isLanguageChooserAvailable,
        isResumePasswordOptionAvailable,
        isTTSAvailable,
        isWithdrawOptionAvailable,
        isZoomFactorChooserAvailable,
    } = props;

    const getLanguageButton = () => {
        return (
            <LanguageButton
                key="language_button"
                clickHandler={navigationService.navigateToOptionsLanguage}
                a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.selectLanguage') })}
            />
        );
    };

    const getBackToDashboardButton = () => {
        return (
            <BackToDashboardButton
                key="back_to_dashboard_button"
                clickHandler={navigationService.navigateToDashboardTicket}
                a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.backToDashboard') })}
            />
        );
    };

    const getOptionsList = () => {
        const list = [];

        if (sessionReader.isContextAnyViewDeliveryType()) {
            return buildContextViewButtonList(list);
        }

        const mainList = [];

        if (isDeliverOptionAvailable) {
            mainList.push(
                <SubmitButton
                    key="submit_now_button"
                    clickHandler={navigationService.navigateToDeliveringPage}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.goToSubmissionPage') })}
                />,
            );
        }

        const mainGroup = <OptionsGroup buttons={mainList} key="optionGroupMainList" />;
        list.push(mainGroup);

        const settingsList = [];
        if (isLanguageChooserAvailable) {
            settingsList.push(getLanguageButton());
        }

        if (isContrastChooserAvailable) {
            settingsList.push(
                <ContrastButton
                    key="contrast_button"
                    clickHandler={navigationService.navigateToOptionsContrast}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.adjustContrastSettings') })}
                />
            );
        }

        if (isTTSAvailable) {
            settingsList.push(
                <TTSButton
                    key="tts_button"
                    clickHandler={navigationService.navigateToOptionsTTS}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.tts') })}
                />,
            );
        }

        if (isZoomFactorChooserAvailable) {
            settingsList.push(
                <ZoomFactorButton
                    key="zoom_factor_button"
                    clickHandler={navigationService.navigateToZoomFactor}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.adjustTextSize') })}
                />
            );
        }

        if (isInstructionsOptionAvailable) {
            settingsList.push(
                <InstructionsButton
                    key="instructions_button"
                    clickHandler={navigationService.navigateToTestInstructions}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.testInstructions') })}
                />
            );
        }

        if (settingsList.length > 0) {
            const settingsGroup = <OptionsGroup buttons={settingsList} key="optionsGroupSettingsList" />;
            list.push(settingsGroup);
        }

        const deliveryList = [];
        if (isDeliverBlankOptionAvailable) {
            deliveryList.push(
                <DeliverBlankButton
                    key="deliver_blank_button"
                    clickHandler={navigationService.navigateToOptionsDeliverBlank}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.submitBlankAnswers') })}
                />,
            );
        }

        if (isWithdrawOptionAvailable) {
            deliveryList.push(
                <WithdrawButton
                    key="withdraw_button"
                    clickHandler={navigationService.navigateToOptionsWithdraw}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.withdrawFromTest') })}
                />,
            );
        }

        if (isExitSebOptionAvailable) {
            deliveryList.push(
                <ExitSebButton
                    key="exit_seb_button"
                    clickHandler={navigationService.navigateToOptionsExitSeb}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.exitSeb') })}
                />,
            );
        }

        if (isExitChromeAppAvailable) {
            deliveryList.push(
                <ExitChromeAppButton
                    key="exit_chrome_app_button"
                    clickHandler={navigationService.navigateToOptionsExitChromeApp}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.exitChromeApp') })}
                />,
            );
        }

        if (isFileDeliveryOptionAvailable) {
            deliveryList.push(
                <FileDeliveryButton
                    key="file_delivery_button"
                    clickHandler={navigationService.navigateToOptionsFileDelivery}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.saveSubmissionAsFile') })}
                />,
            );
        }

        if (isFileDeliveryChromeAppOptionAvailable) {
            deliveryList.push(
                <FileDeliveryChromeAppButton
                    key="file_delivery_chrome_app_button"
                    clickHandler={navigationService.navigateToOptionsFileDeliveryChromeApp}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.fileDeliveryChromeApp') })}
                />,
            );
        }

        if (isAddExtraTimeAvailable) {
            deliveryList.push(
                <ExtraTimeButton
                    key="add_extra_time_button"
                    clickHandler={navigationService.navigateToOptionsAddExtraTime}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.addExtraTimeTitle') })}
                />
            );
        }

        if (isHideTimeRemainingAvailable) {
            deliveryList.push(
                <HideTimeRemainingButton
                    key="distraction_free_mode_button"
                    clickHandler={navigationService.navigateToOptionsHideTimeRemaining}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.toggleRemainingTimeVisibility') })}
                />,
            );
        }

        if (isBackToDashboardOptionAvailable) {
            deliveryList.push(getBackToDashboardButton());
        }

        if (deliveryList.length > 0 && !sessionReader.isContextReadinessCheck()) {
            list.push(
                <OptionsGroup buttons={deliveryList} key="optionsGroupDeliveryList" />
            );
        }

        const invigilatorList = [];
        if (isInvigilatorActionsOptionAvailable) {
            invigilatorList.push(
                <InvigilatorActionsButton
                    key="invigilator_options_button"
                    clickHandler={navigationService.navigateToInvigilatorOptions}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.invigilator-options') })}
                />,
            );
        }

        if (invigilatorList.length > 0) {
            list.push(
                <OptionsGroup buttons={invigilatorList} key="optionsGroupInvigilatorList" />
            );
        }

        return list;
    };


    const getResumePasswordOptionsList = () => {
        const list = [];
        const mainList = [];
        if (isResumePasswordOptionAvailable) {
            mainList.push(
                <ResumePasswordButton
                    key="resume_seb_button"
                    clickHandler={navigationService.navigateToOptionsResumePassword}
                    a11yAttributes={getA11yAttributesV2({ 'aria-label': t('options.resumeSeb') })}
                />,
            );
        }

        list.push(
            <OptionsGroup buttons={mainList} key="optionGroupMainList" />
        );

        return list;
    };

    const buildContextViewButtonList = (list) => {
        const mainList = [];

        if (isLanguageChooserAvailable) {
            mainList.push(
                getLanguageButton(),
            );
        }
        if (sessionReader.isContextViewOwnDelivery()) {
            mainList.push(
                getBackToDashboardButton(),
            );
        }

        const mainGroup = <OptionsGroup buttons={mainList} key="optionGroupMainList" />;
        list.push(mainGroup);
        return list;
    };

    const isRequiringPasswordForResume = sessionReader.isRequiringPasswordForResume();
    const optionsList = isRequiringPasswordForResume ? getResumePasswordOptionsList() : getOptionsList();

    return (
        <div>
            <OptionsTopBar
                title={t('options.optionsHeader')}
                showExitButton={!isRequiringPasswordForResume}
                showBackToDashboardButton={isRequiringPasswordForResume}
                exitButtonProps={getA11yAttributesV2({ 'aria-label': t('options.closeOptionsMenu') })}
            />
            <div
                className={styles.mainContentArea}
                role="main"
                {...getA11yAttributesV2({ 'aria-label': t('options.optionsHeader') })}
            >
                {optionsList}
            </div>
        </div>
    );
};

CandidateOptions.propTypes = {
    isAddExtraTimeAvailable: PropTypes.bool.isRequired,
    isBackToDashboardOptionAvailable: PropTypes.bool.isRequired,
    isContrastChooserAvailable: PropTypes.bool.isRequired,
    isDeliverBlankOptionAvailable: PropTypes.bool.isRequired,
    isDeliverOptionAvailable: PropTypes.bool.isRequired,
    isExitChromeAppAvailable: PropTypes.bool.isRequired,
    isExitSebOptionAvailable: PropTypes.bool.isRequired,
    isFileDeliveryChromeAppOptionAvailable: PropTypes.bool.isRequired,
    isFileDeliveryOptionAvailable: PropTypes.bool.isRequired,
    isHideTimeRemainingAvailable: PropTypes.bool.isRequired,
    isInstructionsOptionAvailable: PropTypes.bool.isRequired,
    isInvigilatorActionsOptionAvailable: PropTypes.bool.isRequired,
    isLanguageChooserAvailable: PropTypes.bool.isRequired,
    isResumePasswordOptionAvailable: PropTypes.bool.isRequired,
    isTTSAvailable: PropTypes.bool.isRequired,
    isWithdrawOptionAvailable: PropTypes.bool.isRequired,
    isZoomFactorChooserAvailable: PropTypes.bool.isRequired,
};

const mapStateToProps = state => optionsListSelector.selectProps(state);
const OptionsList = connect(mapStateToProps)(CandidateOptions);

export default OptionsList;
